import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Services from "./pages/Services";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Tracking from "./pages/Tracking";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import DriverLogin from "./pages/DriverLogin";
import DriverRegister from "./pages/DriverRegister";
import DriverSignup from "./pages/DriverSignup";
import AdminLogin from "./pages/AdminLogin";
import CustomerDashboard from "./pages/CustomerDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminDeliveries from "./pages/AdminDeliveries";
import AdminJobDetail from "./pages/AdminJobDetail";
import AdminManagePOD from "./pages/AdminManagePOD";
import JobReport from "./pages/JobReport";
import DriverDeliveries from "./pages/DriverDeliveries";
import DriverJobStops from "./pages/DriverJobStops";
import DriverStop from "./pages/DriverStop";
import ClientTracking from "./pages/ClientTracking";
import CustomerDeliveryProof from "./pages/CustomerDeliveryProof";
import Conditions from "./pages/Conditions";
import Compliance from "./pages/Compliance";
import ComplianceCenter from "./pages/ComplianceCenter";
import Sops from "./pages/Sops";
import LegalDoc from "./pages/LegalDoc";
import ServicePage from "./pages/ServicePage";
import CityPage from "./pages/CityPage";
import NotFound from "./pages/NotFound";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SEO_SERVICES, SEO_CITIES } from "./data/seo";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    const loginPath =
      role === "admin" ? "/admin-login" : role === "driver" ? "/driver-login" : "/login";
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Layout({ children, bare }) {
  if (bare) return children;
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/services" element={<Layout><Services /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/get-a-quote" element={<Layout><Contact /></Layout>} />
      <Route path="/tracking" element={<Layout><Tracking /></Layout>} />
      <Route path="/conditions" element={<Layout><Conditions /></Layout>} />
      <Route path="/compliance" element={<Layout><Compliance /></Layout>} />
      <Route path="/sops" element={<Layout><Sops /></Layout>} />
      <Route path="/privacy" element={<Layout><LegalDoc /></Layout>} />
      <Route path="/hipaa-notice" element={<Layout><LegalDoc /></Layout>} />
      <Route path="/accessibility" element={<Layout><LegalDoc /></Layout>} />
      <Route path="/customer-terms" element={<Layout><LegalDoc /></Layout>} />
      <Route path="/driver-terms" element={<Layout><LegalDoc /></Layout>} />

      {/* SEO service landing pages */}
      {SEO_SERVICES.map((s) => (
        <Route key={s.slug} path={`/${s.slug}`} element={<Layout><ServicePage /></Layout>} />
      ))}

      {/* SEO city landing pages */}
      {SEO_CITIES.map((c) => (
        <Route key={c.slug} path={`/${c.slug}`} element={<Layout><CityPage /></Layout>} />
      ))}

      {/* Auth (bare layout) */}
      <Route path="/login" element={<Layout bare><Login /></Layout>} />
      <Route path="/register" element={<Layout bare><Register /></Layout>} />
      <Route path="/forgot-password" element={<Layout bare><ForgotPassword /></Layout>} />
      <Route path="/driver-login" element={<Layout bare><DriverLogin /></Layout>} />
      <Route path="/driver-register" element={<Layout bare><DriverRegister /></Layout>} />
      <Route path="/driver-signup" element={<Layout bare><DriverSignup /></Layout>} />
      <Route path="/admin-login" element={<Layout bare><AdminLogin /></Layout>} />

      {/* Dashboards */}
      <Route path="/customer-dashboard" element={<Protected role="customer"><Layout><CustomerDashboard /></Layout></Protected>} />
      <Route path="/compliance-center" element={<Protected><Layout><ComplianceCenter /></Layout></Protected>} />
      <Route path="/driver-dashboard" element={<Protected role="driver"><Layout><DriverDashboard /></Layout></Protected>} />
      <Route path="/admin-dashboard" element={<Protected role="admin"><Layout><AdminDashboard /></Layout></Protected>} />
      <Route path="/admin/deliveries" element={<Protected role="admin"><Layout><AdminDeliveries /></Layout></Protected>} />
      <Route path="/admin/deliveries/:jobId" element={<Protected role="admin"><Layout><AdminJobDetail /></Layout></Protected>} />
      <Route path="/admin/deliveries/:jobId/pod" element={<Protected role="admin"><Layout><AdminManagePOD /></Layout></Protected>} />
      <Route path="/admin/deliveries/:jobId/report" element={<Protected role="admin"><Layout bare><JobReport /></Layout></Protected>} />
      <Route path="/driver/deliveries" element={<Protected role="driver"><Layout><DriverDeliveries /></Layout></Protected>} />
      <Route path="/driver/deliveries/:jobId" element={<Protected role="driver"><Layout bare><DriverJobStops /></Layout></Protected>} />
      <Route path="/driver/deliveries/:jobId/stop/:stopId" element={<Protected role="driver"><Layout bare><DriverStop /></Layout></Protected>} />

      {/* Public client tracking (secure token) */}
      <Route path="/track/:jobRef/:token" element={<Layout bare><ClientTracking /></Layout>} />
      {/* Secure customer Delivery Proof page (token only) */}
      <Route path="/delivery/:token" element={<CustomerDeliveryProof />} />

      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
